import { Button } from "primereact/button";
import { Message } from "primereact/message";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  Dropdown,
  ErrorState,
  FormField,
  StateMessage,
} from "@/ds";
import { CampoDecimal } from "@/shared/components";
import { EscopoCartao } from "../escopo-cartao";
import modal from "../planejamento-controle.modal.module.scss";
import tabela from "../planejamento-controle.tabela.module.scss";
import {
  APLICAR_LABEL,
  CANCELAR_LABEL,
  CARREGANDO_LABEL,
  MENSALIZACAO_CATEGORIA,
  MENSALIZACAO_TITLE,
  MENSALIZACAO_TOTAL,
  PROJECAO_NO_DESTINO_TITLE,
  SELECIONE_PLACEHOLDER,
} from "./ajustar-projecao-transferencia-dialog.constants";
import styles from "./ajustar-projecao-transferencia-dialog.module.scss";
import type { AjustarProjecaoTransferenciaDialogViewModel } from "./types-ajustar-projecao-transferencia-dialog";

const RODAPE_DIALOGO = { footer: { className: modal.dialogoRodape } };
const PAINEL = { panel: { className: modal.painel } };
const ROTULO = { label: modal.rotuloCampo };

type Bloco = AjustarProjecaoTransferenciaDialogViewModel["blocos"][number];

function Categoria({ bloco, desabilitado }: { bloco: Bloco; desabilitado: boolean }) {
  return (
    <section className={styles.categoria} data-testid={bloco.ids.cartao}>
      <h4 className={styles.categoriaTitulo}>{bloco.titulo}</h4>
      <FormField.Root classNames={ROTULO} disabled={desabilitado} help={bloco.naOrigemDistribuicao}>
        <FormField.Label>{bloco.distribuicaoLabel}</FormField.Label>
        <FormField.Control>
          <Dropdown
            appendTo="self"
            pt={PAINEL}
            value={bloco.distribuicaoId}
            options={bloco.distribuicoes}
            placeholder={SELECIONE_PLACEHOLDER}
            onChange={(evento) => bloco.escolherDistribuicao(evento.value)}
            data-testid={bloco.ids.distribuicao}
          />
        </FormField.Control>
        <FormField.Help data-testid={bloco.ids.distribuicaoOrigem} />
      </FormField.Root>
      <FormField.Root classNames={ROTULO} disabled={desabilitado} help={bloco.naOrigemCurva}>
        <FormField.Label>{bloco.curvaLabel}</FormField.Label>
        <FormField.Control>
          <Dropdown
            appendTo="self"
            pt={PAINEL}
            value={bloco.curvaId}
            options={bloco.curvas}
            placeholder={SELECIONE_PLACEHOLDER}
            onChange={(evento) => bloco.escolherCurva(evento.value)}
            data-testid={bloco.ids.curva}
          />
        </FormField.Control>
        <FormField.Help data-testid={bloco.ids.curvaOrigem} />
      </FormField.Root>
    </section>
  );
}

export function AjustarProjecaoTransferenciaDialogView({
  formId,
  titulo,
  cartao,
  resumo,
  carregandoOpcoes,
  blocos,
  fatorNaoSeAplica,
  fator,
  mensalizacao,
  erro,
  recarregar,
  aplicarDesabilitado,
  aplicar,
  cancelar,
  descarte,
}: AjustarProjecaoTransferenciaDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="xl"
        className={modal.dialogoFicha}
        contentClassName={modal.corpo}
        onHide={cancelar}
        focusOnShow={false}
        header={<DialogHeader title={titulo} />}
        pt={RODAPE_DIALOGO}
        footer={
          <DialogFooter
            className={modal.rodape}
            cancelLabel={CANCELAR_LABEL}
            confirmLabel={APLICAR_LABEL}
            confirmButtonProps={{ disabled: aplicarDesabilitado }}
            formId={formId}
            onCancel={cancelar}
            testId="ajustar"
          />
        }
        data-testid="ajustar-dialog"
      >
        <form
          id={formId}
          className={styles.form}
          onSubmit={(evento) => {
            evento.preventDefault();
            aplicar();
          }}
          noValidate
        >
          <EscopoCartao
            {...cartao}
            testIds={{
              cartao: "ajustar-cartao",
              projecao: "ajustar-projecao",
              nome: "ajustar-nome",
              contexto: "ajustar-contexto",
            }}
          />
          {resumo ? (
            <section className={styles.resumo} data-testid="ajustar-resumo">
              <h3 className={styles.secao}>{resumo.titulo}</h3>
              <div className={tabela.tabelaWrap}>
                <table className={tabela.tabela} aria-label={resumo.titulo}>
                  <thead>
                    <tr>
                      {resumo.colunas.map((coluna) => (
                        <th key={coluna} scope="col">
                          {coluna}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resumo.linhas.map((linha) => (
                      <tr key={linha.tipo}>
                        <td>{linha.tipo}</td>
                        {linha.celulas.map((celula, indice) => (
                          <td key={resumo.colunas[indice + 1]}>{celula}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
          <h3 className={styles.secao}>{PROJECAO_NO_DESTINO_TITLE}</h3>
          {carregandoOpcoes ? (
            <StateMessage variant="loading" title={CARREGANDO_LABEL} testId="ajustar-loading" />
          ) : null}
          <div className={styles.categorias}>
            {blocos.map((bloco) => (
              <Categoria key={bloco.chave} bloco={bloco} desabilitado={carregandoOpcoes} />
            ))}
          </div>

          {fatorNaoSeAplica ? (
            <Message
              className="p-message-block"
              severity="info"
              text={fatorNaoSeAplica}
              data-testid="ajustar-fator-nao-se-aplica"
            />
          ) : null}
          {fator ? (
            <section className={styles.fator} data-testid="ajustar-fator">
              <h4 className={styles.categoriaTitulo}>{fator.titulo}</h4>
              <p className={styles.texto}>{fator.texto}</p>
              <p className={styles.subtitulo} data-testid="ajustar-fator-subtitulo">
                {fator.subtitulo}
              </p>
              <div className={styles.segmentos} role="group" aria-label={fator.titulo}>
                {fator.opcoes.map((opcao) => (
                  <Button
                    key={opcao.valor}
                    type="button"
                    size="small"
                    text={fator.selecionado !== opcao.valor}
                    severity="secondary"
                    label={opcao.label}
                    aria-pressed={fator.selecionado === opcao.valor}
                    onClick={() => fator.escolher(opcao.valor)}
                    data-testid={opcao.testId}
                  />
                ))}
              </div>
              {fator.campo ? (
                <div className={styles.fatorCampo}>
                  {fator.campo.aviso ? (
                    <p className={styles.texto} data-testid="ajustar-fator-sem-idade">
                      {fator.campo.aviso}
                    </p>
                  ) : null}
                  <CampoDecimal
                    label={fator.campo.label}
                    casas={2}
                    value={fator.campo.valor}
                    onChange={fator.campo.digitar}
                    error={fator.campo.erro}
                    testId="ajustar-fator-percentual"
                  />
                </div>
              ) : null}
              {fator.valorEscopo ? (
                <p className={styles.texto} data-testid="ajustar-fator-valor">
                  {fator.valorEscopo}
                </p>
              ) : null}
              {fator.recalculado ? (
                <p className={styles.texto} data-testid="ajustar-fator-recalculado">
                  {fator.recalculado}
                </p>
              ) : null}
            </section>
          ) : null}

          <section data-testid="ajustar-mensalizacao">
            <h3 className={styles.secao}>{MENSALIZACAO_TITLE}</h3>
            <div className={tabela.tabelaWrap}>
              <table className={tabela.tabela}>
                <thead>
                  <tr>
                    <th scope="col">{MENSALIZACAO_CATEGORIA}</th>
                    {mensalizacao.competencias.map((competencia) => (
                      <th key={competencia} scope="col">
                        {competencia}
                      </th>
                    ))}
                    <th scope="col">{MENSALIZACAO_TOTAL}</th>
                  </tr>
                </thead>
                <tbody>
                  {mensalizacao.linhas.map((linha) => (
                    <tr key={linha.nome} data-testid={linha.testId}>
                      <td>{linha.nome}</td>
                      {linha.valores.map((valor, indice) => (
                        <td key={mensalizacao.competencias[indice]}>{valor}</td>
                      ))}
                      <td>{linha.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {mensalizacao.calculando ? (
              <StateMessage
                variant="loading"
                title={CARREGANDO_LABEL}
                testId="ajustar-calculando"
              />
            ) : null}
          </section>

          {erro ? <ErrorState message={erro} onRetry={recarregar} testId="ajustar-erro" /> : null}
        </form>
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
